import React, { useEffect, useState } from "react";
import { CreditCard, Check, Loader2, ExternalLink, CalendarClock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ErrorState } from "@/components/PageState";
import { useAuth } from "@/context/AuthContext";
import { RESTRICTION_COPY, RESTRICTION_CTA } from "@/lib/trialCopy";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_LABEL = {
  active: { label: "Active", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  trialing: { label: "Free trial", cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
  past_due: { label: "Payment failed", cls: "bg-red-500/10 text-red-600 dark:text-red-400" },
  cancelled: { label: "Cancelled", cls: "bg-muted text-muted-foreground" },
};

function money(amount, currency) {
  const sym = currency === "USD" ? "$" : currency === "INR" ? "₹" : "";
  return `${sym}${Number(amount).toFixed(0)}`;
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function Billing() {
  const { loadMe } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/billing/me");
      setData(data);
    } catch (e) {
      toast.error("Failed to load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const checkout = async () => {
    setBusy("checkout");
    try {
      const { data: res } = await api.post("/billing/checkout");
      if (res.payment_link) {
        window.open(res.payment_link, "_blank", "noopener");
        toast.success("Opening secure payment page…");
      } else {
        toast.error("Payment link unavailable. Please try again.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
    } finally {
      setBusy("");
    }
  };

  const changePlan = async (plan) => {
    if (plan === data.plan) return;
    setBusy(plan);
    try {
      await api.post("/billing/change-plan", { plan });
      toast.success("Plan updated");
      await load();
      if (loadMe) await loadMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change plan");
    } finally {
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription will cancel at cycle end");
      await load();
      if (loadMe) await loadMe();
    } catch (e) {
      toast.error("Failed to cancel");
    } finally {
      setBusy("");
      setCancelOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) {
    return (
      <ErrorState
        title="Billing is unavailable"
        description="We couldn't load your subscription. Try again, or contact support if this continues."
        onRetry={load}
        testid="billing-error"
      />
    );
  }

  const status = STATUS_LABEL[data.subscription_status] || STATUS_LABEL.trialing;
  const inTrial = data.subscription_status === "trialing" && !data.restricted;
  const showSetup = data.razorpay_configured && !inTrial && (
    data.restricted || data.subscription_status === "past_due" || data.subscription_status === "cancelled"
  );

  return (
    <div data-testid="billing-page">
      <div className="mb-8">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Account</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight lg:text-4xl">Billing &amp; subscription</h1>
        <p className="mt-2 text-sm text-muted-foreground">Manage your plan, payment method and billing schedule.</p>
      </div>

      {/* Current plan card */}
      <div className="mb-8 rounded-2xl border border-border bg-card p-6" data-testid="billing-current-plan">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-xl font-bold" data-testid="billing-plan-name">
                    {data.plan_name}
                  </h2>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${status.cls}`} data-testid="billing-status">
                    {status.label}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {data.launch_offer && data.list_amount != null && (
                    <span className="mr-1.5 line-through decoration-2" data-testid="billing-list-amount">
                      {money(data.list_amount, data.currency)}
                    </span>
                  )}
                  {money(data.amount, data.currency)}
                  <span className="text-xs"> {data.currency}/mo</span>
                  {data.launch_offer && (
                    <span className="ml-2 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                      Launch offer
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">
                {data.subscription_status === "trialing" ? "Trial ends" : "Next billing"}
              </p>
              <p className="text-sm font-semibold" data-testid="billing-next-date">{fmt(data.next_billing_at)}</p>
            </div>
          </div>
        </div>

        {showSetup && (
          <div className="mt-5 flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground">
              {RESTRICTION_COPY[data.restriction_reason] || RESTRICTION_COPY.trial_expired}
            </p>
            <Button onClick={checkout} disabled={busy === "checkout"} className="gap-2 font-semibold" data-testid="billing-checkout">
              {busy === "checkout" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              {RESTRICTION_CTA[data.restriction_reason] || RESTRICTION_CTA.trial_expired}
            </Button>
          </div>
        )}
      </div>

      {/* Plan selector */}
      <h2 className="mb-3 font-display text-lg font-bold">Change plan</h2>
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="billing-plans">
        {data.plans.map((p) => {
          const current = p.id === data.plan;
          return (
            <div
              key={p.id}
              className={`rounded-xl border p-5 transition-colors ${
                current ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/30"
              }`}
              data-testid={`billing-plan-${p.id}`}
            >
              <p className="font-display text-base font-bold">{p.name}</p>
              {data.launch_offer && p.list_amount != null && (
                <p className="mt-1 text-xs text-muted-foreground line-through decoration-2">
                  {money(p.list_amount, p.currency)}
                </p>
              )}
              <p className="mt-0.5 text-2xl font-bold tracking-tight">
                {money(p.amount, p.currency)}
                <span className="text-sm font-normal text-muted-foreground">/mo</span>
              </p>
              <Button
                variant={current ? "outline" : "default"}
                size="sm"
                disabled={current || busy === p.id}
                onClick={() => changePlan(p.id)}
                className="mt-4 w-full gap-2 font-semibold"
                data-testid={`billing-switch-${p.id}`}
              >
                {busy === p.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : current ? (
                  <>
                    <Check className="h-4 w-4" /> Current
                  </>
                ) : (
                  "Switch"
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Cancel */}
      {data.subscription_status !== "cancelled" && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="billing-cancel-section">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-display text-sm font-bold">Cancel subscription</p>
              <p className="text-sm text-muted-foreground">
                Your data is preserved. Access continues until the end of the billing cycle.
              </p>
            </div>
            <Button
              variant="outline"
              className="gap-2 text-destructive hover:text-destructive"
              onClick={() => setCancelOpen(true)}
              data-testid="billing-cancel-button"
            >
              <XCircle className="h-4 w-4" /> Cancel plan
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="billing-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll keep access until the end of the current cycle. Your data won't be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-cancel-dismiss">Keep plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={cancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="billing-cancel-confirm"
            >
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
